import { CustomExercise } from '@cf-shared/data-model/exercise';

// Pure: compute the set of user-owned UDE ids that the picker should hide because the
// user's coach has forked the same source. The user's UDE doc remains in `userdb-*` — only
// filtered out of the picker UI (Phase 3 re-surfaces them on detach).
//
// Pre-condition contract: callers MUST pass `coachAuthorIdentityHash = undefined` when the
// user has no coach. Without that guard, the rule "hide UDEs whose `authorIdentityHash`
// equals the coach's hash" degrades to "hide UDEs whose `authorIdentityHash` is undefined"
// (every fresh user fork before publish stamps the field) — see Phase 2 PRD §9.
export function buildHiddenUserUdesByCoach(
  customExercises: CustomExercise[],
  coachAuthorIdentityHash: string | undefined,
): Set<string> {
  const hidden = new Set<string>();
  if (!coachAuthorIdentityHash) return hidden;

  // first pass — collect per-source map of coach-owned forks. From-scratch coach UDEs
  // (no `local.sourceExerciseId`) have nothing to compete with and are excluded.
  const coachOwnedByLineage = new Map<string, CustomExercise>();
  for (const ude of customExercises) {
    if (ude.virtuallyDeleted) continue;
    if (ude.authorIdentityHash !== coachAuthorIdentityHash) continue;
    const sourceId = ude.local?.sourceExerciseId;
    if (!sourceId) continue;
    coachOwnedByLineage.set(sourceId, ude);
  }

  // second pass — hide any user UDE whose source is in the coach's map.
  for (const ude of customExercises) {
    if (ude.virtuallyDeleted) continue;
    if (ude.authorIdentityHash === coachAuthorIdentityHash) continue;
    const sourceId = ude.local?.sourceExerciseId;
    if (!sourceId) continue;
    if (coachOwnedByLineage.has(sourceId)) {
      hidden.add(ude._id);
    }
  }

  return hidden;
}
